import {
  BookmarkEntity,
  BookmarkConstant,
  DataAlreadyExistsException,
  DataNotFoundException,
  LimitExceededException,
  ResponseValueObject,
  StorageException,
} from '@gishub/domain';
import { BboxValueObject } from '@gishub/domain/models/value-objects/bbox-value-object';
import { IBookmarkDatasource } from './i-bookmark-datasource';

export class LocalStorageBookmarkDatasource implements IBookmarkDatasource {
  private readonly storage: Storage;
  private readonly storageKey = 'ghp_bookmarks';

  constructor(storage: Storage = localStorage) {
    this.storage = storage;
  }

  private readAll(): BookmarkEntity[] {
    try {
      const raw = this.storage.getItem(this.storageKey);
      if (!raw) return [];
      const parsed: any[] = JSON.parse(raw);
      return parsed.map(p => new BookmarkEntity({
        ...p,
        id: typeof p.id === 'number' ? p.id : null,
        hash: p.hash ?? (typeof p.id === 'string' ? p.id : LocalStorageBookmarkDatasource.generateHash()),
        bbox: new BboxValueObject(p.bbox),
      }));
    } catch {
      return [];
    }
  }

  private static generateHash(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID().replace(/-/g, '');
    }
    return `bk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  async getAll(username: string): Promise<ResponseValueObject<BookmarkEntity[]>> {
    return new ResponseValueObject(true, this.readAll());
  }

  private persist(list: BookmarkEntity[]): void {
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(list));
    } catch (e) {
      throw new StorageException(e instanceof Error ? e.message : undefined);
    }
  }

  async getById(id: string | number): Promise<ResponseValueObject<BookmarkEntity>> {
    const found = this.readAll().find(b => b.hash === id || b.id === id);
    if (!found) throw new DataNotFoundException('Marcador no encontrado');
    return new ResponseValueObject(true, found);
  }

  async create(entity: BookmarkEntity): Promise<ResponseValueObject<BookmarkEntity>> {
    const list = this.readAll();
    if (list.length >= BookmarkConstant.MAX_PER_USER) {
      throw new LimitExceededException(`Límite de ${BookmarkConstant.MAX_PER_USER} marcadores alcanzado`);
    }
    const hash = entity.hash || LocalStorageBookmarkDatasource.generateHash();
    if (list.some(b => b.hash === hash)) {
      throw new DataAlreadyExistsException('El marcador ya existe');
    }
    const created = new BookmarkEntity({ ...entity, id: null, hash });
    list.push(created);
    this.persist(list);
    return new ResponseValueObject(true, created);
  }

  async update(entity: BookmarkEntity): Promise<ResponseValueObject<BookmarkEntity>> {
    const list = this.readAll();
    const index = list.findIndex(b => b.hash === entity.hash);
    if (index < 0) throw new DataNotFoundException('Marcador no encontrado');
    const updated = new BookmarkEntity({ ...entity, id: list[index].id });
    list[index] = updated;
    this.persist(list);
    return new ResponseValueObject(true, updated);
  }

  async delete(entity: BookmarkEntity): Promise<ResponseValueObject<boolean>> {
    const list = this.readAll().filter(b => b.hash !== entity.hash);
    this.persist(list);
    return new ResponseValueObject(true, true);
  }
}
